// Owner: S10. Identity, access & administration endpoints.
//   import { accessApi } from '@dataroom/api-client/access'; const a = accessApi(api.http);
import type { MeDTO } from '@dataroom/core';
import type {
  AcceptInviteInput,
  AuditEventDetailDTO,
  AuditFacetsDTO,
  AuditPageDTO,
  AuditQuery,
  AuditStreamDTO,
  AuditStreamSettings,
  AuthProvidersDTO,
  BreakGlassDTO,
  CreateRoleInput,
  EditableRecordsDTO,
  EffectiveAccessDTO,
  GeneralDTO,
  GroupMappingDTO,
  IdentityProviderDTO,
  IdentityProviderInput,
  InviteMembersInput,
  InviteResultDTO,
  ListMembersQuery,
  LoginMethodsDTO,
  MemberAdminDTO,
  MemberWorkspaceDTO,
  MembersListDTO,
  PasswordLoginInput,
  PolicyScope,
  PrivacyRequestDTO,
  PrivacyRequestInput,
  RecordPolicyDTO,
  RoleDetailDTO,
  RoleSummaryDTO,
  SaveRoleInput,
  ScimTokenDTO,
  SecurityDTO,
  SecuritySettings,
  TeamDTO,
  TeamInput,
  UpdateGeneralInput,
  UpdateInfoDTO,
  UpdateMemberInput,
  ViewAsDTO,
} from '@dataroom/core/access';
import type { FilterNode } from '@dataroom/core';
import {
  accessExplainContract,
  auditEventContract,
  auditFacetsContract,
  auditListContract,
  auditSavedFiltersContract,
  deleteAuditFilterContract,
  installationOperatorsContract,
  instanceSettingsContract,
  licenseApplyContract,
  licensePreviewContract,
  saveAuditFilterContract,
} from '@dataroom/contracts/access';
import type { AccessExplainInput, SaveAuditFilterInput } from '@dataroom/core/access';
import { type Http, type QueryParams, buildQuery, seg } from './http';
import { createCall } from './typed';

export type InviteInfoDTO = {
  workspace: string;
  email: string;
  name: string;
  role: string;
  invitedBy: string | null;
  expiresAt: string;
  providers: AuthProvidersDTO['providers'];
  requireSso: boolean;
};

const q = (v: object | undefined): QueryParams => (v ?? {}) as QueryParams;

export const accessApi = (http: Http) => {
  const call = createCall(http);
  return {
    auth: {
      providers: (workspace?: string) => http.get<AuthProvidersDTO>('/auth/providers', { workspace }),
      methods: (email: string, workspace?: string) =>
        http.post<LoginMethodsDTO>('/auth/login/methods', { email, ...(workspace ? { workspace } : {}) }),
      login: (input: PasswordLoginInput) => http.post<MeDTO>('/auth/login', input),
      invite: (token: string) => http.get<InviteInfoDTO>(seg`/auth/invites/${token}`),
      /** Join with a local account (password, and two-factor when required). Starts a session. */
      acceptInvite: (token: string, input: AcceptInviteInput) =>
        http.post<MeDTO>(seg`/auth/invites/${token}/accept`, input),
      /** Browser navigation target that starts OIDC sign-in. */
      oidcStartUrl: (providerId: string, redirect?: string, baseUrl = '/api/v1') =>
        `${baseUrl}${seg`/auth/oidc/${providerId}/start`}${buildQuery({ redirect })}`,
    },
    me: () => http.get<EffectiveAccessDTO>('/access/me'),
    /** Ids (of the given ones) the caller may edit. */
    editable: (objectKey: string, ids: string[]) =>
      http.post<EditableRecordsDTO>(seg`/access/editable/${objectKey}`, { ids }),
    /** Workspace switcher: workspaces of your organization where you are an active member. */
    workspaces: {
      list: () => http.get<MemberWorkspaceDTO[]>('/me/workspaces'),
      switch: (id: string) => http.post<MeDTO>(seg`/me/workspaces/${id}/switch`),
    },
    viewAs: (memberId: string) => http.get<ViewAsDTO>(seg`/access/view-as/${memberId}`),
    /** Test access: may the member view, edit or delete the record, and why. */
    explain: (input: AccessExplainInput) => call(accessExplainContract, { body: input }),
    members: {
      list: (query?: ListMembersQuery) => http.get<MembersListDTO>('/members', q(query)),
      exportUrl: (query?: ListMembersQuery, baseUrl = '/api/v1') =>
        `${baseUrl}/members/export.csv${buildQuery(q(query))}`,
      update: (id: string, input: UpdateMemberInput) =>
        http.patch<MemberAdminDTO>(seg`/members/${id}`, input),
      invite: (input: InviteMembersInput) => http.post<InviteResultDTO>('/members/invite', input),
      resendInvite: (id: string) => http.post<{ inviteUrl: string }>(seg`/members/${id}/resend-invite`),
      revokeInvite: (id: string) => http.delete(seg`/members/${id}/invite`),
      revokeSessions: (id: string) => http.post<{ revoked: number }>(seg`/members/${id}/sessions/revoke`),
      setupBreakGlass: (id: string) => http.post<BreakGlassDTO>(seg`/members/${id}/break-glass`),
      removeBreakGlass: (id: string) => http.delete(seg`/members/${id}/break-glass`),
    },
    teams: {
      list: () => http.get<TeamDTO[]>('/teams'),
      create: (input: TeamInput) => http.post<TeamDTO>('/teams', input),
      update: (id: string, input: TeamInput) => http.patch<TeamDTO>(seg`/teams/${id}`, input),
      delete: (id: string) => http.delete(seg`/teams/${id}`),
    },
    roles: {
      list: () => http.get<RoleSummaryDTO[]>('/roles'),
      get: (id: string) => http.get<RoleDetailDTO>(seg`/roles/${id}`),
      create: (input: CreateRoleInput) => http.post<RoleDetailDTO>('/roles', input),
      save: (id: string, input: SaveRoleInput) => http.put<RoleDetailDTO>(seg`/roles/${id}`, input),
      duplicate: (id: string) => http.post<RoleDetailDTO>(seg`/roles/${id}/duplicate`),
      delete: (id: string) => http.delete(seg`/roles/${id}`),
    },
    policies: {
      list: (object?: string) => http.get<RecordPolicyDTO[]>('/policies', { object }),
      save: (object: string, scope: PolicyScope, filter: FilterNode) =>
        http.put<RecordPolicyDTO>(seg`/policies/${object}/${scope}`, { filter }),
      reset: (object: string, scope: PolicyScope) =>
        http.delete<RecordPolicyDTO>(seg`/policies/${object}/${scope}`),
    },
    security: {
      get: () => http.get<SecurityDTO>('/settings/security'),
      update: (patch: Partial<SecuritySettings>) => http.patch<SecuritySettings>('/settings/security', patch),
      createProvider: (input: IdentityProviderInput) =>
        http.post<IdentityProviderDTO>('/settings/security/providers', input),
      updateProvider: (id: string, input: IdentityProviderInput) =>
        http.patch<IdentityProviderDTO>(seg`/settings/security/providers/${id}`, input),
      deleteProvider: (id: string) => http.delete(seg`/settings/security/providers/${id}`),
      testProvider: (id: string, redirect?: string) =>
        http.post<{ url: string }>(
          seg`/settings/security/providers/${id}/test`,
          redirect ? { redirect } : {},
        ),
      createScimToken: (label: string) =>
        http.post<ScimTokenDTO & { token: string }>('/settings/security/scim-tokens', { label }),
      revokeScimToken: (id: string) => http.delete(seg`/settings/security/scim-tokens/${id}`),
      saveGroupMappings: (mappings: Array<{ group: string; roleId: string }>) =>
        http.put<GroupMappingDTO[]>('/settings/security/group-mappings', { mappings }),
      rotateKey: () =>
        http.post<{ activeVersion: number; rewrapped: number }>('/settings/security/keys/rotate'),
    },
    privacy: {
      list: () => http.get<PrivacyRequestDTO[]>('/privacy-requests'),
      create: (input: PrivacyRequestInput) => http.post<PrivacyRequestDTO>('/privacy-requests', input),
      run: (id: string) => http.post<PrivacyRequestDTO>(seg`/privacy-requests/${id}/run`),
      reject: (id: string, note?: string | null) =>
        http.post<PrivacyRequestDTO>(seg`/privacy-requests/${id}/reject`, { note: note ?? null }),
      downloadUrl: (id: string, baseUrl = '/api/v1') => `${baseUrl}${seg`/privacy-requests/${id}/download`}`,
    },
    general: {
      get: () => http.get<GeneralDTO>('/settings/general'),
      update: (input: UpdateGeneralInput) => http.patch<GeneralDTO>('/settings/general', input),
      scheduleDeletion: (confirm: string) => http.post<GeneralDTO>('/settings/general/deletion', { confirm }),
      cancelDeletion: () => http.delete<GeneralDTO>('/settings/general/deletion'),
      exportUrl: (baseUrl = '/api/v1') => `${baseUrl}/settings/general/export`,
    },
    /** Settings › Installation: license and updates of the installation (instance operators). */
    instance: {
      get: () => call(instanceSettingsContract),
      /** Who runs the installation (for an administrator who is not one of them). */
      operators: () => call(installationOperatorsContract),
      previewLicense: (key: string) => call(licensePreviewContract, { body: { key } }),
      applyLicense: (key: string) => call(licenseApplyContract, { body: { key } }),
      preflight: () => http.post<UpdateInfoDTO>('/settings/general/updates/preflight'),
    },
    audit: {
      list: (query?: AuditQuery): Promise<AuditPageDTO> => call(auditListContract, { query: query ?? {} }),
      get: (id: string): Promise<AuditEventDetailDTO> => call(auditEventContract, { params: { id } }),
      facets: (): Promise<AuditFacetsDTO> => call(auditFacetsContract),
      savedFilters: () => call(auditSavedFiltersContract),
      saveFilter: (input: SaveAuditFilterInput) => call(saveAuditFilterContract, { body: input }),
      deleteFilter: (id: string) => call(deleteAuditFilterContract, { params: { id } }),
      exportUrl: (query?: AuditQuery, baseUrl = '/api/v1') =>
        `${baseUrl}/audit/export.csv${buildQuery(q(query))}`,
      stream: () => http.get<AuditStreamDTO>('/settings/audit-stream'),
      saveStream: (input: AuditStreamSettings) => http.put<AuditStreamDTO>('/settings/audit-stream', input),
      testStream: () =>
        http.post<{ ok: boolean; status: number | null; error: string | null }>(
          '/settings/audit-stream/test',
        ),
    },
  };
};

export type AccessApi = ReturnType<typeof accessApi>;
